// pages/GuildDataService.js (수정된 버전 - 새로운 길드 요구사항 반영)
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  writeBatch
} from "firebase/firestore";

class GuildDataService {
  // 내 길드 정보 가져오기 (멤버 목록 포함)
  async getMyGuild(userId) {
    try {
      // 먼저 guildMembers 컬렉션에서 사용자의 길드 정보 확인
      const memberQ = query(
        collection(db, "guildMembers"), 
        where("userId", "==", userId)
      );
      const memberSnap = await getDocs(memberQ);
      
      if (memberSnap.empty) {
        // guildMembers에 없으면 users 컬렉션에서 확인 (호환성)
        const userDoc = await getDoc(doc(db, "users", userId));
        const userData = userDoc.data();
        
        if (!userData?.guildId) return null;
        
        const guildDoc = await getDoc(doc(db, "guilds", userData.guildId));
        if (!guildDoc.exists()) return null;
        
        return {
          guild: { id: guildDoc.id, ...guildDoc.data() },
          members: [] // 빈 배열로 반환
        };
      }

      const memberData = memberSnap.docs[0].data();
      const guildDoc = await getDoc(doc(db, "guilds", memberData.guildId));
      
      if (!guildDoc.exists()) return null;

      const guildData = { id: guildDoc.id, ...guildDoc.data() };

      // 길드 멤버들 정보 가져오기
      const membersQ = query(
        collection(db, "guildMembers"),
        where("guildId", "==", memberData.guildId)
      );
      const membersSnap = await getDocs(membersQ);
      
      const members = [];
      for (const memberDoc of membersSnap.docs) {
        const member = memberDoc.data();
        // 사용자 추가 정보 가져오기
        const userDoc = await getDoc(doc(db, "users", member.userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          members.push({
            id: member.userId,
            ...member,
            nickname: userData.nickname || member.nickname,
            profileImg: userData.profileImg || "",
            lastLoginDate: userData.lastLoginDate || member.lastLoginDate || new Date(),
            contribution: member.contribution || 0,
            guildTier: member.role || member.tier || "임시회원", // 호환성
            guildRole: member.guildRole || "",
            guildWins: member.guildWins || 0,
            guildLosses: member.guildLosses || 0,
            joinedGuildAt: member.joinDate || member.joinedGuildAt || new Date()
          });
        }
      }

      // 멤버들을 등급순으로 정렬
      const tierOrder = { "길드장": 0, "부길드장": 1, "정회원": 2, "임시회원": 3 };
      members.sort((a, b) => {
        const aTier = tierOrder[a.guildTier] || 4;
        const bTier = tierOrder[b.guildTier] || 4;
        return aTier - bTier;
      });

      return { guild: guildData, members };
    } catch (error) {
      console.error("길드 정보 로드 실패:", error);
      throw error;
    }
  }

  // 모든 길드 목록 가져오기 (필터링용)
  async getAllGuilds() {
    try {
      const guildsSnap = await getDocs(collection(db, "guilds"));
      return guildsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("길드 목록 로드 실패:", error);
      return [];
    }
  }

  // 길드 생성 (새로운 요구사항 반영)
  async createGuild(guildData, creatorId, creatorNickname) {
    try {
      console.log("🔧 길드 생성 시작:", { guildData, creatorId, creatorNickname });
      
      const guildDoc = await addDoc(collection(db, "guilds"), {
        name: guildData.name,
        description: guildData.intro || guildData.description || "",
        sport: guildData.sport, // 하나의 운동만 선택
        region: guildData.region || "", // 활동 지역
        leaderId: creatorId,
        leaderName: creatorNickname,
        logoUrl: guildData.coverUrl || guildData.logoUrl || "",
        maxMembers: guildData.maxMembers || 50,
        memberCount: 1,
        mmrRequirement: guildData.mmrRequirement || 0,
        autoApproval: guildData.autoApproval || false,
        joinMessage: guildData.joinMessage || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // 길드 MMR 및 통계
        guildMMR: 1000,
        wins: 0,
        losses: 0,
        point: 0,
        // 회계 관리
        treasury: {
          initialAmount: guildData.treasury?.initialAmount || 0,
          currentAmount: guildData.treasury?.initialAmount || 0,
          records: []
        },
        // 친구 길드 목록
        friendGuilds: [],
        friendGuildsList: {},
        hashtags: guildData.hashtags || []
      });

      console.log("✅ 길드 문서 생성 완료:", guildDoc.id);

      // 길드장을 guildMembers에 추가
      await addDoc(collection(db, "guildMembers"), {
        guildId: guildDoc.id,
        userId: creatorId,
        nickname: creatorNickname,
        role: "길드장", // 기존 필드명 유지
        tier: "길드장", // 호환성
        guildRole: "", // 길드장은 역할 없음
        contribution: 0,
        joinDate: serverTimestamp(),
        guildWins: 0,
        guildLosses: 0
      });

      console.log("✅ 길드 멤버 추가 완료");

      // 사용자 정보도 업데이트 (호환성)
      try {
        await updateDoc(doc(db, "users", creatorId), {
          guildId: guildDoc.id,
          guildName: guildData.name,
          guildTier: "길드장",
          guildRole: "",
          joinedGuildAt: serverTimestamp()
        });
        console.log("✅ 사용자 정보 업데이트 완료");
      } catch (userUpdateError) {
        console.log("⚠️ 사용자 정보 업데이트 실패 (무시):", userUpdateError);
      }

      return { id: guildDoc.id, ...guildData };
    } catch (error) {
      console.error("❌ 길드 생성 실패:", error);
      throw error;
    }
  }

  // 길드 멤버 등급 관리 (길드장만 가능)
  async updateMemberTier(guildId, memberId, newTier, role = "") {
    try {
      // guildMembers 컬렉션에서 멤버 찾기
      const memberQ = query(
        collection(db, "guildMembers"),
        where("guildId", "==", guildId),
        where("userId", "==", memberId)
      );
      const memberSnap = await getDocs(memberQ);
      
      if (!memberSnap.empty) {
        const memberDocId = memberSnap.docs[0].id;
        await updateDoc(doc(db, "guildMembers", memberDocId), {
          role: newTier, // 기존 필드명 사용
          tier: newTier, // 호환성
          guildRole: role,
          updatedAt: serverTimestamp()
        });
      }

      // users 컬렉션도 업데이트 (호환성)
      const userDoc = doc(db, "users", memberId);
      await updateDoc(userDoc, {
        guildTier: newTier,
        guildRole: role,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error("멤버 등급 변경 실패:", error);
      throw error;
    }
  }

  // 회계 기록 추가 (길드장, 부길드장만 가능)
  async addTreasuryRecord(guildId, recordData) {
    try {
      const guildRef = doc(db, "guilds", guildId);
      const guildDoc = await getDoc(guildRef);
      const currentTreasury = guildDoc.data().treasury || { records: [], currentAmount: 0 };

      const newRecord = {
        id: Date.now().toString(),
        date: new Date(),
        amount: recordData.amount,
        purpose: recordData.purpose,
        participants: recordData.participants || [],
        type: recordData.type, // 'income' or 'expense'
        addedBy: recordData.addedBy,
        createdAt: serverTimestamp()
      };

      const newAmount = recordData.type === 'income' 
        ? currentTreasury.currentAmount + recordData.amount
        : currentTreasury.currentAmount - recordData.amount;

      await updateDoc(guildRef, {
        "treasury.currentAmount": newAmount,
        "treasury.records": [...currentTreasury.records, newRecord],
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error("회계 기록 추가 실패:", error);
      throw error;
    }
  }

  // 길드 친구 등록 (길드장, 부길드장만 가능)
  async addGuildFriend(guildId, targetGuildId) {
    try {
      const batch = writeBatch(db);
      
      // 양방향으로 친구 추가
      const guild1Ref = doc(db, "guilds", guildId);
      const guild2Ref = doc(db, "guilds", targetGuildId);
      
      batch.update(guild1Ref, {
        friendGuilds: increment(1),
        [`friendGuildsList.${targetGuildId}`]: true
      });
      
      batch.update(guild2Ref, {
        friendGuilds: increment(1),
        [`friendGuildsList.${guildId}`]: true
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error("길드 친구 등록 실패:", error);
      throw error;
    }
  }

  // 길드 랭킹전 신청 (길드장만 가능)
  async applyGuildRankedMatch(guildId, preferredDates) {
    try {
      const guildDoc = await getDoc(doc(db, "guilds", guildId));
      const guildData = guildDoc.data();

      await addDoc(collection(db, "guildMatchRequests"), {
        guildId,
        guildName: guildData.name,
        sport: guildData.sport,
        region: guildData.region,
        guildMMR: guildData.guildMMR || 1000,
        preferredDates,
        status: "waiting", // waiting, matched, completed, cancelled
        createdAt: serverTimestamp(),
        matchedWith: null,
        matchedAt: null
      });

      return true;
    } catch (error) {
      console.error("길드 랭킹전 신청 실패:", error);
      throw error;
    }
  }

  // 길드 매칭 수락/거절
  async respondToGuildMatch(requestId, guildId, response) {
    try {
      if (response === "accept") {
        await updateDoc(doc(db, "guildMatchRequests", requestId), {
          status: "matched",
          matchedWith: guildId,
          matchedAt: serverTimestamp()
        });
      } else {
        await deleteDoc(doc(db, "guildMatchRequests", requestId));
      }
      return true;
    } catch (error) {
      console.error("길드 매칭 응답 실패:", error);
      throw error;
    }
  }

  // 길드전 참가자 제출
  async submitGuildMatchParticipants(matchId, guildId, participants) {
    try {
      await updateDoc(doc(db, "guildMatches", matchId), {
        [`${guildId}_participants`]: participants,
        [`${guildId}_submitted`]: true,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error("참가자 제출 실패:", error);
      throw error;
    }
  }

  // 길드전 결과 입력
  async submitGuildMatchResult(matchId, matchResults) {
    try {
      const batch = writeBatch(db);
      
      // 매치 결과 업데이트
      const matchRef = doc(db, "guildMatches", matchId);
      batch.update(matchRef, {
        results: matchResults,
        status: "completed",
        completedAt: serverTimestamp()
      });

      // 각 참가자의 개인 승패 기록 업데이트
      matchResults.forEach(result => {
        const player1Ref = doc(db, "users", result.player1Id);
        const player2Ref = doc(db, "users", result.player2Id);
        
        if (result.winner === result.player1Id) {
          batch.update(player1Ref, { guildWins: increment(1) });
          batch.update(player2Ref, { guildLosses: increment(1) });
        } else {
          batch.update(player1Ref, { guildLosses: increment(1) });
          batch.update(player2Ref, { guildWins: increment(1) });
        }
      });

      // 길드 MMR 업데이트 계산
      const guild1Wins = matchResults.filter(r => r.guild1Winner).length;
      const guild2Wins = matchResults.length - guild1Wins;
      
      const guild1Ref = doc(db, "guilds", matchResults[0].guild1Id);
      const guild2Ref = doc(db, "guilds", matchResults[0].guild2Id);
      
      if (guild1Wins > guild2Wins) {
        batch.update(guild1Ref, { 
          wins: increment(1), 
          guildMMR: increment(30),
          point: increment(3)
        });
        batch.update(guild2Ref, { 
          losses: increment(1), 
          guildMMR: increment(-20),
          point: increment(1)
        });
      } else {
        batch.update(guild1Ref, { 
          losses: increment(1), 
          guildMMR: increment(-20),
          point: increment(1)
        });
        batch.update(guild2Ref, { 
          wins: increment(1), 
          guildMMR: increment(30),
          point: increment(3)
        });
      }

      await batch.commit();
      return true;
    } catch (error) {
      console.error("길드전 결과 입력 실패:", error);
      throw error;
    }
  }

  // 길드 게시글 가져오기
  async getGuildPosts(guildId) {
    try {
      const postsQ = query(
        collection(db, "guildPosts"),
        where("guildId", "==", guildId),
        orderBy("createdAt", "desc"),
        limit(10)
      );
      
      const postsSnap = await getDocs(postsQ);
      return postsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("게시글 로드 실패:", error);
      return [];
    }
  }

  // 길드 채팅 가져오기
  async getGuildChats(guildId) {
    try {
      const chatsQ = query(
        collection(db, "guildChats"),
        where("guildId", "==", guildId),
        orderBy("createdAt", "desc"),
        limit(50)
      );
      
      const chatsSnap = await getDocs(chatsQ);
      const chats = chatsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      return chats.reverse(); // 오래된 순서로
    } catch (error) {
      console.error("채팅 로드 실패:", error);
      return [];
    }
  }

  // 게시글 작성
  async createPost(guildId, authorId, authorName, postData) {
    try {
      await addDoc(collection(db, "guildPosts"), {
        guildId,
        author: authorName,
        userId: authorId,
        title: postData.title,
        content: postData.content,
        category: postData.category,
        createdAt: serverTimestamp(),
        likedBy: [],
        dislikedBy: [],
        comments: []
      });
      return true;
    } catch (error) {
      console.error("게시글 작성 실패:", error);
      throw error;
    }
  }

  // 채팅 전송
  async sendChat(guildId, userId, userNickname, message) {
    try {
      await addDoc(collection(db, "guildChats"), {
        guildId,
        userId,
        userNickname,
        message,
        createdAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error("채팅 전송 실패:", error);
      throw error;
    }
  }
  // 길드 정보 수정
async updateGuild(guildId, updateData) {
  try {
    const guildRef = doc(db, "guilds", guildId);
    await updateDoc(guildRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error("길드 정보 수정 실패:", error);
    throw error;
  }
}

  // 길드 가입 신청
  async applyToGuild(userId, userNickname, guild) {
    try {
      // 중복 신청 방지: 이미 가입 신청(pending) 상태이거나 이미 길드 멤버인 경우 신청 불가
      const q = query(
        collection(db, "notifications"),
        where("from", "==", userId),
        where("guildId", "==", guild.id),
        where("type", "==", "guild_join_request"),
        where("status", "==", "pending")
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        throw new Error("이미 가입 신청이 진행 중입니다.");
      }
      // 이미 길드 멤버인 경우(추가적으로 체크)
      const guildDoc = await getDoc(doc(db, "guilds", guild.id));
      if (guildDoc.exists()) {
        const data = guildDoc.data();
        if (Array.isArray(data.members) && data.members.includes(userId)) {
          throw new Error("이미 해당 길드에 가입되어 있습니다.");
        }
      }
      await addDoc(collection(db, "notifications"), {
        from: userId,
        fromNickname: userNickname,
        to: guild.leaderId,
        guildName: guild.name,
        type: "guild_join_request",
        status: "pending",
        guildId: guild.id,
        createdAt: new Date()
      });
      return true;
    } catch (error) {
      console.error("길드 가입 신청 실패:", error);
      throw error;
    }
  }

  // 길드 삭제
  async deleteGuild(guildId) {
    // 1. 모든 길드 멤버의 길드 정보 제거
    const membersQuery = query(
      collection(db, "guildMembers"),
      where("guildId", "==", guildId)
    );
    const membersSnap = await getDocs(membersQuery);
    for (const memberDoc of membersSnap.docs) {
      const memberData = memberDoc.data();
      await updateDoc(doc(db, "users", memberData.userId), {
        guildId: null,
        guildName: null,
        guildTier: null,
        guildRole: null
      });
      await deleteDoc(memberDoc.ref);
    }

    // 2. 길드 관련 데이터 삭제
    const collectionsToClean = [
      "guildAttendance",
      "guildMatches",
      "guildPosts",
      "guildChats",
      "guildTournaments",
      "guildMatchRequests"
    ];
    for (const collectionName of collectionsToClean) {
      const dataQuery = query(
        collection(db, collectionName),
        where("guildId", "==", guildId)
      );
      const dataSnap = await getDocs(dataQuery);
      for (const dataDoc of dataSnap.docs) {
        await deleteDoc(dataDoc.ref);
      }
    }

    // 3. 길드 문서 삭제
    await deleteDoc(doc(db, "guilds", guildId));
  }
}
  
export const guildDataService = new GuildDataService();